import { FC, MouseEvent, useCallback, useMemo, useState } from 'react';
import Modal, { ModalHandler } from 'components/base/Modal';
import { Fade } from 'components/Transition';
import useMediaQuery from 'hooks/useMediaQuery';
import type { CountryData } from 'types/common';
import classlist from 'utils/classlist';
import PortalMobile from './PortalMobile';
import PortalPC from './PortalPC';
import styles from './style.scss';

export interface CountryCodeProps {
    value?: string;
    onChange?(value: string): void;
    disabled?: boolean;
    placeholder?: string;
    isInvalid?: boolean;
    countryData: CountryData[]; // 国家数据列表
}

const CountryCode: FC<CountryCodeProps> = ({ value, disabled, placeholder, onChange, isInvalid, countryData }) => {
    const [show, setShow] = useState(false);
    const isSmScreen = useMediaQuery();
    const countryName = useMemo(() => {
        let name: string = '';

        if (value && countryData.length > 0) {
            for (let index = 0; index < countryData.length; index++) {
                const list = countryData[index];
                const curItem = list.items.find(item => value === item.country_code);

                if (curItem) {
                    name = curItem.country_name || curItem.country || '';

                    break;
                }
            }
        }

        return name;
    }, [value, countryData]);

    const handleShowPanel = useCallback(
        (isShow: boolean) => {
            if (!disabled) {
                if (isSmScreen) {
                    Modal.openSlideModal({
                        component: ({ close, dismiss }: ModalHandler) => (
                            <PortalMobile
                                countryData={countryData}
                                countryCode={value}
                                close={close}
                                dismiss={dismiss}
                            />
                        ),
                        responsive: true,
                        className: styles.mobileModal
                    })
                        .result.then(res => {
                            onChange?.(res);
                        })
                        .catch(() => {});
                } else {
                    setShow(isShow);
                }
            }
        },
        [disabled, countryData, isSmScreen, value, onChange]
    );

    const handleClickCode = (event: MouseEvent) => {
        event.stopPropagation();

        handleShowPanel(!show);
    };

    // 改变表单值，隐藏弹框
    const handleChange = (value: string) => {
        onChange?.(value);

        handleShowPanel(false);
    };

    return (
        <div className={styles.root}>
            <div className={classlist('form-control', { disabled, 'is-invalid': isInvalid })} onClick={handleClickCode}>
                <span className={classlist(styles.code, { [styles.empty]: !countryName })}>
                    {countryName || placeholder}
                </span>
            </div>
            <Fade in={!isSmScreen && show} timeout={200}>
                <PortalPC
                    handleShow={handleShowPanel}
                    onChange={handleChange}
                    countryData={countryData}
                    countryCode={value}
                />
            </Fade>
        </div>
    );
};

export default CountryCode;
